/*
 * Smores "Our Story" — two-card deck slider
 *
 * Both slides stay in the same DOM position (CSS grid stacks them).
 * On activation we set a `--depth` custom property on each slide:
 * the active slide is depth 0 (front) and the other is depth 1
 * (peeks up from behind). CSS transitions handle the animation.
 *
 * Click a dot or press arrow keys (← ↑ for previous, → ↓ for next)
 * to advance. The active slide is always the only one accepting
 * pointer events.
 */
class SmoresStory extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.smores-story__slide')];
    this.dots = [...this.querySelectorAll('.smores-story__dot')];
    if (this.slides.length === 0) return;

    this.index = 0;
    this.autoplayDelay = 5000; // advance every 5s
    this.autoplayTimer = null;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

    // "Read more" link(s) on the first slide → jump to the second slide.
    this.querySelectorAll('[data-story-next]').forEach((btn) =>
      btn.addEventListener('click', () => this.go(1))
    );

    // Pause autoplay while the user is hovering or focused inside the deck,
    // or when the tab is in the background; resume when they leave.
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());
    this._onVisibility = () => (document.hidden ? this.stopAutoplay() : this.startAutoplay());
    document.addEventListener('visibilitychange', this._onVisibility);

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); this.go(this.index + 1); }
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   { e.preventDefault(); this.go(this.index - 1); }
    });

    // Touch swipe (mobile): a horizontal drag advances slides. Passive so
    // we never block vertical page scrolling; we only act on a clear
    // horizontal swipe (dx beats dy) past a small threshold.
    let startX = 0, startY = 0, tracking = false;
    this.addEventListener('touchstart', (e) => {
      const t = e.changedTouches[0];
      startX = t.clientX; startY = t.clientY; tracking = true;
    }, { passive: true });
    this.addEventListener('touchend', (e) => {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
        this.go(this.index + (dx < 0 ? 1 : -1)); // swipe left → next
      }
    }, { passive: true });

    this.go(0);
  }

  go(i) {
    const total = this.slides.length;
    this.index = ((i % total) + total) % total;

    this.slides.forEach((slide, si) => {
      // Cyclic distance from the active slide: 0 = front, 1 = peeking behind.
      const depth = (si - this.index + total) % total;
      slide.style.setProperty('--depth', String(depth));
      slide.dataset.depth = String(depth);
      slide.dataset.active = String(depth === 0);
      slide.setAttribute('aria-hidden', String(depth !== 0));
    });

    this.dots.forEach((dot, di) => {
      dot.setAttribute('aria-current', String(di === this.index));
    });

    // Every advance (manual or auto) resets the 5s autoplay clock.
    this.startAutoplay();
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.reduceMotion || this.slides.length < 2) return;
    this.autoplayTimer = window.setTimeout(() => this.go(this.index + 1), this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    document.removeEventListener('visibilitychange', this._onVisibility);
  }
}

if (!customElements.get('smores-story')) {
  customElements.define('smores-story', SmoresStory);
}